import { useLanguageAwareRouting } from '@gofranz/common-components';
import {
  Alert,
  Anchor,
  Button,
  Code,
  CopyButton,
  Group,
  List,
  Paper,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import { IconCheck, IconCopy, IconInfoCircle, IconLock } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/Common/PageHeader';
import { API_BASE_URL } from '../../constants';
import { mcpConnectCommand, mcpEndpointUrl } from '../../lib/mcp-connect';
import { useRustyState } from '../../state';

// The endpoint is fixed per deployment, so this page is static: no API call
// can tell the user anything the URL does not already.
const MCP_URL = mcpEndpointUrl(API_BASE_URL);
const CONNECT_COMMAND = mcpConnectCommand(MCP_URL);

function CopyableCommand({ value, disabled }: { value: string; disabled?: boolean }) {
  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Code style={{ overflowX: 'auto', whiteSpace: 'nowrap', flex: 1 }}>{value}</Code>
        <CopyButton value={value} timeout={2000}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="left">
              <ActionIcon
                color={copied ? 'teal' : 'gray'}
                variant="subtle"
                onClick={copy}
                disabled={disabled}
                aria-label="Copy"
              >
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Paper>
  );
}

export function AccountMcpPage() {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  // The endpoint only accepts tokens from the SSO issuer, so an account with no
  // linked SSO identity cannot connect at all — whatever it logged in with today.
  const [hasSso, setHasSso] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const identities = await useRustyState.getState().api.listIdentities();
        if (!cancelled) {
          setHasSso(identities.some((i) => i.method === 'Oidc'));
        }
      } catch (error) {
        console.error('Failed to load linked identities:', error);
        if (!cancelled) {
          setHasSso(true); // Don't lock the page out on a transient failure.
        }
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  const unavailable = hasSso === false;

  return (
    <>
      <PageHeader title={t('account_mcp.title')} subtitle={t('account_mcp.subtitle')} />

      {hasSso === null && <Skeleton height={72} radius="md" mb="lg" />}

      {unavailable && (
        <Alert
          icon={<IconLock size={18} />}
          color="yellow"
          title={t('account_mcp.ssoRequiredTitle')}
          mb="lg"
        >
          <Stack gap="sm" align="flex-start">
            <Text size="sm">{t('account_mcp.ssoRequiredBody')}</Text>
            <Button
              component="a"
              href={`#${createLanguageURL('/account/profile')}`}
              size="compact-sm"
              variant="light"
              color="yellow"
            >
              {t('account_mcp.ssoRequiredCta')}
            </Button>
          </Stack>
        </Alert>
      )}

      <Stack
        gap="lg"
        aria-disabled={unavailable}
        style={
          unavailable
            ? { opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }
            : undefined
        }
      >
        <Stack gap="xs">
          <Text fw={600}>{t('account_mcp.endpointHeading')}</Text>
          <CopyableCommand value={MCP_URL} disabled={unavailable} />
        </Stack>

        <Stack gap="xs">
          <Text fw={600}>{t('account_mcp.connectHeading')}</Text>
          <Text size="sm" c="dimmed">
            {t('account_mcp.connectBody')}
          </Text>
          <CopyableCommand value={CONNECT_COMMAND} disabled={unavailable} />
        </Stack>

        <Stack gap="xs">
          <Text fw={600}>{t('account_mcp.scopesHeading')}</Text>
          <Text size="sm" c="dimmed">
            {t('account_mcp.scopesBody')}
          </Text>
          <List size="sm" spacing={4}>
            <List.Item>
              <Code>formshive:forms:read</Code> — {t('account_mcp.scopeFormsRead')}
            </List.Item>
            <List.Item>
              <Code>formshive:messages:read</Code> — {t('account_mcp.scopeMessagesRead')}
            </List.Item>
            <List.Item>
              <Code>formshive:messages:write</Code> — {t('account_mcp.scopeMessagesWrite')}
            </List.Item>
            <List.Item>
              <Code>formshive:integrations:read</Code> — {t('account_mcp.scopeIntegrationsRead')}
            </List.Item>
            <List.Item>
              <Code>formshive:analytics:read</Code> — {t('account_mcp.scopeAnalyticsRead')}
            </List.Item>
          </List>
        </Stack>

        <Alert icon={<IconInfoCircle size={18} />} title={t('account_mcp.writeNoticeTitle')}>
          {t('account_mcp.writeNoticeBody')}
        </Alert>

        <Text size="sm" c="dimmed">
          {t('account_mcp.privacyNote')}{' '}
          <Anchor href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer">
            modelcontextprotocol.io
          </Anchor>
        </Text>
      </Stack>
    </>
  );
}
